package org.multiplatform.kotfolio.sections

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.models.Section
import org.multiplatform.kotfolio.models.Theme
import org.multiplatform.kotfolio.util.Constants
import org.multiplatform.kotfolio.util.Res
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowRight
import com.varabyte.kobweb.silk.components.icons.fa.FaGithub
import com.varabyte.kobweb.silk.components.icons.fa.FaGooglePlay
import com.varabyte.kobweb.silk.components.icons.fa.FaLinkedin
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.multiplatform.kotfolio.rememberAppState

private val AMBER_500 = Color.rgb(245, 158, 11)

@Composable
fun aboutMeSection() {
    val breakpoint = rememberAppState().breakpoint
    val isDesktop = breakpoint >= Breakpoint.MD

    Column(
        modifier = Modifier
            .id(Section.About.id)
            .fillMaxWidth()
            .backgroundColor(Theme.Background.rgb)
            .padding(topBottom = if (isDesktop) 150.px else 80.px)
            .styleModifier {
                // Subtle geometric network pattern background as seen in the image
                property(
                    "background-image",
                    "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.02) 1px, transparent 0)",
                )
                property("background-size", "40px 40px")
            },
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SimpleGrid(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(Constants.SECTION_WIDTH.px)
                .padding(leftRight = if (isDesktop) 10.percent else 5.percent),
            numColumns = numColumns(base = 1, lg = 2),
        ) {
            // Left Column: Text Content
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(right = if (isDesktop) 60.px else 0.px),
                horizontalAlignment = if (isDesktop) Alignment.Start else Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center,
            ) {
                // Headline
                P(
                    attrs = Modifier
                        .fontFamily(Constants.FONT_FAMILY)
                        .fontSize(if (isDesktop) 54.px else 36.px)
                        .fontWeight(FontWeight.Black)
                        .color(Colors.White)
                        .lineHeight(1.1)
                        .margin(bottom = 32.px)
                        .textAlign(if (isDesktop) TextAlign.Start else TextAlign.Center)
                        .toAttrs(),
                ) {
                    val parts = listOf(
                        "Our ",
                        "focused on ",
                        "Full-stack ",
                        "Web development, ",
                        "Seller portal management. ",
                    )
                    parts.forEach { part ->
                        if (part == "Developer " || part == "Full-stack " || part == "Web development.") {
                            Span(attrs = Modifier.color(AMBER_500).toAttrs()) { Text(part) }
                        } else {
                            Text(part)
                        }
                    }
                }

                // Bio Description (New Detailed Content)
                Column(
                    modifier = Modifier.margin(bottom = 48.px),
                    horizontalAlignment = if (isDesktop) Alignment.Start else Alignment.CenterHorizontally,
                ) {
                    Constants.BIO_PARAGRAPHS.forEach { text ->
                        P(
                            attrs = Modifier
                                .fontFamily(Constants.FONT_FAMILY)
                                .fontSize(18.px)
                                .color(Color.rgba(255, 255, 255, 0.7f))
                                .lineHeight(1.6)
                                .margin(bottom = 24.px)
                                .textAlign(if (isDesktop) TextAlign.Start else TextAlign.Center)
                                .toAttrs(),
                        ) {
                            Text(text)
                        }
                    }
                }

                // CTA and Work Button
                Row(
                    modifier = Modifier.fillMaxWidth().margin(top = 40.px),
                    horizontalArrangement = if (isDesktop) Arrangement.Start else Arrangement.Center,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    // View My Work Button
                    Link(
                        path = "#${Section.Portfolio.id}",
                        modifier = Modifier
                            .backgroundColor(Color.rgb(180, 170, 160)) // Taupe/Beige from image
                            .padding(leftRight = 32.px, topBottom = 16.px)
                            .borderRadius(50.px)
                            .color(Color.rgb(60, 40, 30)) // Dark brown text
                            .fontWeight(FontWeight.Bold)
                            .fontSize(18.px)
                            .styleModifier {
                                property("text-decoration", "none")
                                property("transition", "all 0.3s ease")
                            },
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Text("View My Work")
                            FaArrowRight(modifier = Modifier.margin(left = 12.px))
                        }
                    }

                    // Vertical Divider
                    Box(
                        modifier = Modifier
                            .width(1.px)
                            .height(40.px)
                            .backgroundColor(Color.rgba(255, 255, 255, 0.1f))
                            .margin(leftRight = 32.px),
                    )

                    // Social Icons Row
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        val socials = listOf(
                            Constants.LINKEDIN_LINK to @Composable { FaLinkedin(size = IconSize.LG) },
                            Constants.GITHUB_LINK to @Composable { FaGithub(size = IconSize.LG) },
                            Constants.GOOGLE_DEVELOPER_LINK to @Composable { FaGooglePlay(size = IconSize.LG) },
                            //Constants.FACEBOOK_LINK to @Composable { FaFacebook(size = IconSize.LG) },
                        )

                        socials.forEachIndexed { index, [link, icon] ->
                            Link(
                                path = link,
                                modifier = Modifier
                                    .margin(right = if (index != socials.size - 1) 24.px else 0.px)
                                    .color(AMBER_500)
                                    .styleModifier {
                                        property("transition", "transform 0.3s ease")
                                    },
                            ) {
                                icon()
                            }
                        }
                    }
                }
            }

            // Right Column: Image
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = if (isDesktop) 0.px else 60.px),
                contentAlignment = Alignment.Center,
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .maxWidth(500.px)
                        .aspectRatio(1f) // Square image as in the sample
                        .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.1f))
                        .styleModifier {
                            property("filter", "grayscale(100%)")
                            property("overflow", "hidden")
                        },
                ) {
                    Image(
                        modifier = Modifier.fillMaxSize().styleModifier {
                            property("object-fit", "cover")
                        },
                        src = Res.Image.MY_IMAGE,
                        description = "Portrait of Kotlin Multiplatform Developer",
                    )
                }
            }
        }

        // Expertise Section (What I Focus On)
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(Constants.SECTION_WIDTH.px)
                .margin(top = if (isDesktop) 100.px else 60.px)
                .padding(leftRight = if (isDesktop) 10.percent else 5.percent),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Span(
                attrs = Modifier
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(16.px)
                    .fontWeight(FontWeight.Bold)
                    .color(AMBER_500)
                    .margin(bottom = 12.px)
                    .styleModifier {
                        property("letter-spacing", "4px")
                        property("text-transform", "uppercase")
                    }
                    .toAttrs(),
            ) {
                Text("What I Focus On")
            }

            P(
                attrs = Modifier
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(32.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Colors.White)
                    .margin(bottom = 48.px)
                    .toAttrs(),
            ) {
                Text("Technical Specializations")
            }

            SimpleGrid(
                modifier = Modifier.fillMaxWidth(),
                numColumns = numColumns(base = 1, md = 2, lg = 4),
            ) {
                val focusAreas = listOf(
                    Triple("", "Jetpack Compose", "Declarative framework for sharing UIs across multiple platforms. Based on Kotlin, Kotlin multiplatform UI and Jetpack Compose."),
                    Triple("", "Compose Multiplatform", "Streamline efficient multiplatform development, ensuring quality and reducing the cost of serving customers."),
                    Triple("", "KobWeb", "It is an opinionated framework built on top of Compose HTML. Creating everything you need to build rich, websites, while being able to leverage the greater Kotlin ecosystem. It support koin and ktor etc."),
                    Triple("", "Flutter", "I am dedicated Flutter app developer, I can provide high-quality flutter app development services flawlessly."),
                    Triple("", "FlutterFlow", "Our approach centers around leveraging the power of Flutterflow- A No-code platform. From concept to creation, we harness cutting-edge tools to deliver stunning applications."),
                    Triple("", "React native", "I develop cross-platform mobile apps that offer the best of both worlds – a native-like experience on iOS and Android."),
                    Triple("", "Ecommerce solution", "Comprehensive e-commerce solutions with newest technology & trends that attract targeted audiences to your business."),
                    Triple("", "KVision", "KVision allows you to build modern web applications with the Kotlin language, without any use of HTML, CSS or JavaScript. KVision fully supports both reactive and imperative programming models."),
                    Triple("", "WordPress", "WordPress to your needs, we develop feature-rich and easy-to-manage websites that reflect your brand identity and engage your real world business."),
                    Triple("", "Web developments", "We have developed numerous customs, dynamic and e-commerce websites for all fields industry and clients from all over the world."),
                    Triple("", "Support & Maintenance", "As technologies changes, websites and mobile apps need to update. We Offering 24/7 support system, you can reach us by call or live chat."),

                    )

                focusAreas.forEach { [icon, title, desc] ->
                    ExpertiseCard(icon, title, desc)
                }
            }
        }

        // Philosophy and Interests
        SimpleGrid(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(Constants.SECTION_WIDTH.px)
                .margin(top = 100.px)
                .padding(leftRight = if (isDesktop) 10.percent else 5.percent),
            numColumns = numColumns(base = 1, md = 2),
        ) {
            // Development Philosophy
            Column(
                modifier = Modifier.padding(right = if (isDesktop) 40.px else 0.px, bottom = if (isDesktop) 0.px else 60.px),
            ) {
                TextSectionHeader("Development Philosophy")
                val philosophies = listOf(
                    "Reliable under real-world usage",
                    "Simple for users to understand",
                    "Maintainable for long-term growth",
                    "Built with scalability in mind",
                    "Designed to solve actual operational problems",
                )
                philosophies.forEach { item ->
                    CheckmarkItem(item)
                }
            }

            // Current Interests
            Column(
                modifier = Modifier.padding(left = if (isDesktop) 40.px else 0.px),
            ) {
                TextSectionHeader("Current Interests")
                val interests = listOf(
                    "Kotlin Coroutines & Flows",
                    "Full-stack system architecture",
                    "Infrastructure & self-hosted systems",
                    "Unit testing and software reliability",
                    "Cross-platform development",
                    "Operational and logistics technologies",
                )
                interests.forEach { item ->
                    CheckmarkItem(item)
                }
            }
        }
    }
}

@Composable
private fun TextSectionHeader(text: String) {
    P(
        attrs = Modifier
            .fontFamily(Constants.FONT_FAMILY)
            .fontSize(24.px)
            .fontWeight(FontWeight.Bold)
            .color(Colors.White)
            .margin(bottom = 32.px)
            .toAttrs(),
    ) {
        Text(text)
    }
}

@Composable
private fun CheckmarkItem(text: String) {
    Row(
        modifier = Modifier.margin(bottom = 16.px),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            modifier = Modifier
                .size(8.px)
                .backgroundColor(AMBER_500)
                .borderRadius(50.percent)
                .margin(right = 16.px),
        )
        P(
            attrs = Modifier
                .margin(top = 0.px, bottom = 0.px)
                .fontFamily(Constants.FONT_FAMILY)
                .fontSize(16.px)
                .color(Color.rgba(255, 255, 255, 0.7f))
                .toAttrs(),
        ) {
            Text(text)
        }
    }
}

@Composable
private fun ExpertiseCard(icon: String, title: String, description: String) {
    Column(
        modifier = Modifier
            .margin(all = 12.px)
            .padding(all = 24.px)
            .backgroundColor(Color.rgba(255, 255, 255, 0.02f))
            .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.05f))
            .borderRadius(12.px)
            .styleModifier {
                property("transition", "all 0.3s ease")
            },
    ) {
        //Span(attrs = Modifier.fontSize(32.px).margin(bottom = 16.px).toAttrs()) { Text(icon) }

        P(
            attrs = Modifier
                .margin(top = 0.px, bottom = 8.px)
                .fontFamily(Constants.FONT_FAMILY)
                .fontSize(18.px)
                .fontWeight(FontWeight.Bold)
                .color(Colors.White)
                .toAttrs(),
        ) { Text(title) }
        P(
            attrs = Modifier
                .margin(top = 0.px, bottom = 0.px)
                .fontFamily(Constants.FONT_FAMILY)
                .fontSize(14.px)
                .color(Color.rgba(255, 255, 255, 0.5f))
                .lineHeight(1.5)
                .toAttrs(),
        ) { Text(description) }
    }
}
